import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// Event Images - Update these paths to your actual event images
import heroImage from '../assets/Food/Ayurveda-Restaurant-Lanka-Princess-New-batch-12-51.jpg';
import cookeryImage1 from '../assets/Food/Amuna-Dining-3-2.webp';
import cookeryImage2 from '../assets/River/53.png';
import meditationImage2 from '../assets/River/52.jpeg';
import meditationImage1 from '../assets/River/54.jpg';
import bbqImage1 from '../assets/Food/LK7C010500-04-E-1280-720_70717ba9-b6de-4bf2-a750-34d168591ff8.jpg';
import bbqImage2 from '../assets/Food/rajawewa-resort-facilities-07.jpg';
import boatImage1 from '../assets/Food/image.webp';
import boatImage2 from '../assets/Food/1_explore-bentota-riversafari-tour-with-hotel-pickupdrop-off-800x534.jpg';
import ayurvedaImage1 from '../assets/Ayurveda/67.jpeg';
import ayurvedaImage2 from '../assets/Ayurveda/68.jpeg';
import traditionalImage1 from '../assets/River/55.png';
import traditionalImage2 from '../assets/Food/images.jpeg';

// Animation Variants
const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const fadeInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const scaleUp = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.7, ease: 'easeOut' } },
};

const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.15 },
    },
};

const Events2 = () => {
    const { t } = useTranslation('events2');
    const events = [
        {
            title: t('cookeryTitle'),
            subtitle: t('cookerySubtitle'),
            description: t('cookeryDescription'),
            highlights: [
                t('cookeryHighlight1'),
                t('cookeryHighlight2'),
                t('cookeryHighlight3'),
                t('cookeryHighlight4'),
                t('cookeryHighlight5')
            ],
            image1: cookeryImage1,
            image2: cookeryImage2,
            color: '#BF943A',
            bgGradient: 'from-[#3D2C1F] to-[#1F1F1F]'
        },
        {
            title: t('meditationTitle'),
            subtitle: t('meditationSubtitle'),
            description: t('meditationDescription'),
            highlights: [
                t('meditationHighlight1'),
                t('meditationHighlight2'),
                t('meditationHighlight3'),
                t('meditationHighlight4'),
                t('meditationHighlight5')
            ],
            image1: meditationImage1,
            image2: meditationImage2,
            color: '#96BA33',
            bgGradient: 'from-[#1E2D15] to-[#1F1F1F]'
        },
        {
            title: t('bbqTitle'),
            subtitle: t('bbqSubtitle'),
            description: t('bbqDescription'),
            highlights: [
                t('bbqHighlight1'),
                t('bbqHighlight2'),
                t('bbqHighlight3'),
                t('bbqHighlight4'),
                t('bbqHighlight5')
            ],
            image1: bbqImage1,
            image2: bbqImage2,
            color: '#D4A574',
            bgGradient: 'from-[#2D1F15] to-[#1F1F1F]'
        },
        {
            title: t('boatTitle'),
            subtitle: t('boatSubtitle'),
            description: t('boatDescription'),
            highlights: [
                t('boatHighlight1'),
                t('boatHighlight2'),
                t('boatHighlight3'),
                t('boatHighlight4'),
                t('boatHighlight5')
            ],
            image1: boatImage1,
            image2: boatImage2,
            color: '#7A9629',
            bgGradient: 'from-[#1A2417] to-[#1F1F1F]'
        },
        {
            title: t('ayurvedaLecturesTitle'),
            subtitle: t('ayurvedaLecturesSubtitle'),
            description: t('ayurvedaLecturesDescription'),
            highlights: [
                t('ayurvedaLecturesHighlight1'),
                t('ayurvedaLecturesHighlight2'),
                t('ayurvedaLecturesHighlight3'),
                t('ayurvedaLecturesHighlight4'),
                t('ayurvedaLecturesHighlight5')
            ],
            image1: ayurvedaImage1,
            image2: ayurvedaImage2,
            color: '#A67C52',
            bgGradient: 'from-[#2D2416] to-[#1F1F1F]'
        },
        {
            title: t('traditionalClothsTitle'),
            subtitle: t('traditionalClothsSubtitle'),
            description: t('traditionalClothsDescription'),
            highlights: [
                t('traditionalClothsHighlight1'),
                t('traditionalClothsHighlight2'),
                t('traditionalClothsHighlight3'),
                t('traditionalClothsHighlight4'),
                t('traditionalClothsHighlight5')
            ],
            image1: traditionalImage1,
            image2: traditionalImage2,
            color: '#C9826E',
            bgGradient: 'from-[#3D2820] to-[#1F1F1F]'
        },
    ];

    return (
        <div className="min-h-screen bg-[#1F1F1F] text-white font-redhat">
            {/* Hero Section */}
            <section className="relative h-[60vh] lg:h-[70vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#1F1F1F] z-10"></div>
                <img
                    src={heroImage}
                    alt="Events at Muthumuni Ayurveda River Resort"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={fadeInUp}
                    className="container relative z-20 text-center px-4"
                >
                    <div className="flex items-center justify-center gap-4 mb-6">
                        <div className="h-px w-12 bg-[#BF943A]"></div>
                        <span className="text-[#BF943A] uppercase text-sm md:text-base tracking-wider font-semibold">
                            {t('heroEyebrow')}
                        </span>
                        <div className="h-px w-12 bg-[#BF943A]"></div>
                    </div>
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <h1 className="text-[40px] md:text-[55px] lg:text-[70px] font-bold leading-tight">
                            {t('heroTitle')}
                        </h1>
                    </div>
                    <p className="text-[16px] md:text-[18px] lg:text-[20px] text-gray-300 max-w-3xl mx-auto">
                        {t('heroSubtitle')}
                    </p>

                </motion.div>
            </section>

            {/* Events Sections */}
            {events.map((event, index) => (
                <section
                    key={event.title}
                    className={`relative py-16 lg:py-24 bg-gradient-to-b ${event.bgGradient}`}
                >
                    <div className="container px-4">
                        {index % 2 === 0 ? (
                            // Layout 1: Image on Left, Content on Right
                            <div className="space-y-10 lg:space-y-0">
                                {/* Mobile-first layout (subtitle → title → images → content) */}
                                <div className="lg:hidden space-y-6">
                                    {/* Subtitle + line */}
                                    <div className="flex items-center gap-4">
                                        <div className="h-px w-12" style={{ backgroundColor: event.color }}></div>
                                        <span
                                            className="text-sm uppercase tracking-wider font-semibold"
                                            style={{ color: event.color }}
                                        >
                                            {event.subtitle}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h2 className="text-4xl font-bold leading-tight">
                                        {event.title}
                                    </h2>

                                    {/* Images Grid - two images (primary + secondary) */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInLeft}
                                        className="relative"
                                    >
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="h-[280px] sm:h-[340px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image1}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                            <div className="h-[280px] sm:h-[340px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image2}
                                                    alt={`${event.title} detail`}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Description + highlights - only in mobile flow */}
                                    <div className="space-y-6">
                                        <p className="text-base text-gray-300 leading-relaxed">
                                            {event.description}
                                        </p>

                                        <motion.ul
                                            variants={staggerContainer}
                                            initial="hidden"
                                            whileInView="show"
                                            viewport={{ once: true, amount: 0.3 }}
                                            className="space-y-2"
                                        >
                                            {event.highlights.map((highlight, idx) => (
                                                <motion.li
                                                    key={idx}
                                                    variants={scaleUp}
                                                    className="flex items-center gap-3 text-[15px] text-gray-300"
                                                >
                                                    <span
                                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: event.color }}
                                                    ></span>
                                                    {highlight}
                                                </motion.li>
                                            ))}
                                        </motion.ul>
                                    </div>
                                </div>

                                {/* Desktop layout (lg+) - side by side */}
                                <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
                                    {/* Left - Images */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInLeft}
                                        className="relative"
                                    >
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="h-[400px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image1}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                            <div className="h-[250px] lg:h-[520px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image2}
                                                    alt={`${event.title} detail`}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Right - Content */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInRight}
                                        className="space-y-6"
                                    >
                                        <div>
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className="h-px w-12" style={{ backgroundColor: event.color }}></div>
                                                <span
                                                    className="text-base uppercase tracking-wider font-semibold"
                                                    style={{ color: event.color }}
                                                >
                                                    {event.subtitle}
                                                </span>
                                            </div>
                                            <h2 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                                {event.title}
                                            </h2>
                                            <p className="text-lg text-gray-300 leading-relaxed">
                                                {event.description}
                                            </p>
                                        </div>

                                        <motion.ul
                                            variants={staggerContainer}
                                            initial="hidden"
                                            whileInView="show"
                                            viewport={{ once: true, amount: 0.3 }}
                                            className="space-y-3"
                                        >
                                            {event.highlights.map((highlight, idx) => (
                                                <motion.li
                                                    key={idx}
                                                    variants={scaleUp}
                                                    className="flex items-center gap-3 text-base text-gray-300"
                                                >
                                                    <span
                                                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: event.color }}
                                                    ></span>
                                                    {highlight}
                                                </motion.li>
                                            ))}
                                        </motion.ul>
                                    </motion.div>
                                </div>
                            </div>
                        ) : (
                            // Layout 2: Content on Left, Image on Right
                            <div className="space-y-10 lg:space-y-0">
                                {/* Mobile layout (sm/md) - stacked vertically */}
                                <div className="lg:hidden space-y-6">
                                    {/* Subtitle */}
                                    <div className="flex items-center gap-4">
                                        <div className="h-px w-12" style={{ backgroundColor: event.color }}></div>
                                        <span
                                            className="text-sm uppercase tracking-wider font-semibold"
                                            style={{ color: event.color }}
                                        >
                                            {event.subtitle}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h2 className="text-4xl font-bold leading-tight">
                                        {event.title}
                                    </h2>

                                    {/* Images Grid - two images (primary + secondary) */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInUp}
                                        className="relative"
                                    >
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="h-[280px] sm:h-[340px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image1}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                            <div className="h-[280px] sm:h-[340px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image2}
                                                    alt={`${event.title} detail`}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Description + Highlights */}
                                    <div className="space-y-6">
                                        <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                                            {event.description}
                                        </p>

                                        <motion.ul
                                            variants={staggerContainer}
                                            initial="hidden"
                                            whileInView="show"
                                            viewport={{ once: true, amount: 0.3 }}
                                            className="space-y-3"
                                        >
                                            {event.highlights.map((highlight, idx) => (
                                                <motion.li
                                                    key={idx}
                                                    variants={scaleUp}
                                                    className="flex items-center gap-3 text-[15px] text-gray-300"
                                                >
                                                    <span
                                                        className="w-2 h-2 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: event.color }}
                                                    ></span>
                                                    {highlight}
                                                </motion.li>
                                            ))}
                                        </motion.ul>
                                    </div>
                                </div>

                                {/* Desktop layout (lg+) - side by side */}
                                <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
                                    {/* Images - Left side */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInLeft}
                                        className="relative"
                                    >
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="h-[400px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image1}
                                                    alt={event.title}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                            <div className="h-[250px] lg:h-[520px] rounded-2xl overflow-hidden shadow-2xl group">
                                                <img
                                                    src={event.image2}
                                                    alt={`${event.title} detail`}
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                />
                                            </div>
                                        </div>
                                    </motion.div>

                                    {/* Content - Right side */}
                                    <motion.div
                                        initial="hidden"
                                        whileInView="visible"
                                        viewport={{ once: true, amount: 0.3 }}
                                        variants={fadeInRight}
                                        className="space-y-6"
                                    >
                                        <div>
                                            <div className="flex items-center gap-4 mb-6">
                                                <div className="h-px w-12" style={{ backgroundColor: event.color }}></div>
                                                <span
                                                    className="text-base uppercase tracking-wider font-semibold"
                                                    style={{ color: event.color }}
                                                >
                                                    {event.subtitle}
                                                </span>
                                            </div>

                                            <h2 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
                                                {event.title}
                                            </h2>

                                            <p className="text-lg text-gray-300 leading-relaxed mb-8">
                                                {event.description}
                                            </p>
                                        </div>

                                        <motion.ul
                                            variants={staggerContainer}
                                            initial="hidden"
                                            whileInView="show"
                                            viewport={{ once: true, amount: 0.3 }}
                                            className="space-y-4"
                                        >
                                            {event.highlights.map((highlight, idx) => (
                                                <motion.li
                                                    key={idx}
                                                    variants={scaleUp}
                                                    className="flex items-center gap-4 text-base text-gray-300"
                                                >
                                                    <span
                                                        className="w-3 h-3 rounded-full flex-shrink-0"
                                                        style={{ backgroundColor: event.color }}
                                                    ></span>
                                                    {highlight}
                                                </motion.li>
                                            ))}
                                        </motion.ul>
                                    </motion.div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>
            ))}

            {/* CTA Section */}
            <section className="py-16 lg:py-24 bg-gradient-to-b from-[#1F1F1F] to-[#121212]">
                <div className="container px-4">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={fadeInUp}
                        className="bg-gradient-to-r from-[#285509] to-[#12291f] rounded-3xl p-8 md:p-12 lg:p-16 text-center"
                    >
                        <h2 className="text-[32px] md:text-[42px] lg:text-[52px] font-bold mb-6">
                            {t('ctaTitle')}
                        </h2>
                        <p className="text-[16px] md:text-[18px] lg:text-[20px] text-gray-300 mb-8 max-w-2xl mx-auto">
                            {t('ctaDescription')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                            <a
                                href="/river/contact-us"
                                className="inline-flex items-center gap-2 bg-white text-black px-8 py-4 rounded-full font-bold transition-all duration-300 hover:scale-105 shadow-xl text-[15px] md:text-[16px] uppercase tracking-wider"
                            >
                                {t('ctaContact')}
                                <span>→</span>
                            </a>
                            <a
                                href="/river/rooms"
                                className="inline-flex items-center gap-2 border-2 border-[#BF943A] text-[#BF943A] hover:bg-[#BF943A] hover:text-black px-8 py-4 rounded-full font-bold transition-all duration-300 text-[15px] md:text-[16px] uppercase tracking-wider"
                            >
                                {t('ctaViewRooms')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default Events2;
